/**
 * INT-0011 — makes the landing AI-teaser carry-through VISIBLE during the
 * create wizard. The visitor's description rides localStorage from staty.io
 * through signup to Get Started, but the wizard never acknowledged it — so
 * it read as "my description got lost" (owner repro 2026-09-10). This
 * banner PEEKS at the pending description (never consumes it — Get Started
 * owns removal) and tells the user it's still following them.
 */
import { Sparkles } from 'lucide-react';

export const PENDING_DESC_KEY = 'staty-pending-league-desc';
export const PENDING_SPORT_KEY = 'staty-pending-league-sport';
// Tier 2: the landing teaser's GENERATED structure rides the sign-up URL as
// base64url(JSON) so the wizard's confirm step shows exactly what was
// previewed. Untrusted client input — onboarding/build sanitizes server-side
// (sanitizeOnboardingStructure); the shape check here only fails closed.
export const PENDING_STRUCTURE_KEY = 'staty-pending-league-structure';

export interface PendingStructure {
  name: string;
  contestType: 'tournament' | 'season';
  contests: string[];
  divisions: { name: string; format?: string; capacity?: number }[];
}

// Mirrors the landing encoder (landing-ploy can't depend on shared code):
// base64url → UTF-8 JSON. Returns null on any decode failure.
export function decodePendingStructure(param: string): PendingStructure | null {
  try {
    const bin = atob(param.replace(/-/g, '+').replace(/_/g, '/'));
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    const parsed = JSON.parse(new TextDecoder().decode(bytes));
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.divisions)) return null;
    const divisions = parsed.divisions
      .filter((d: unknown) => d && typeof d === 'object' && typeof (d as { name?: unknown }).name === 'string')
      .slice(0, 20)
      .map((d: { name: string; format?: unknown; capacity?: unknown }) => ({
        name: d.name,
        ...(typeof d.format === 'string' ? { format: d.format } : {}),
        ...(typeof d.capacity === 'number' ? { capacity: d.capacity } : {}),
      }));
    if (!divisions.length) return null;
    return {
      name: typeof parsed.name === 'string' ? parsed.name : '',
      contestType: parsed.contestType === 'tournament' ? 'tournament' : 'season',
      contests: Array.isArray(parsed.contests)
        ? parsed.contests.filter((c: unknown) => typeof c === 'string').slice(0, 12)
        : [],
      divisions,
    };
  } catch {
    return null;
  }
}

export function readPendingStructure(): PendingStructure | null {
  try {
    const raw = localStorage.getItem(PENDING_STRUCTURE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Stored pre-validated by SignUpPage; re-validate cheaply via a re-encode
    // round-trip guard: shape-check the essentials only.
    if (!parsed || !Array.isArray(parsed.divisions) || !parsed.divisions.length) return null;
    return parsed as PendingStructure;
  } catch {
    return null;
  }
}

// The wizard grows a "Confirm your structure" step (4 total) when a carry is
// pending. Predictive on the early screens — if the user later picks an
// unsupported sport the flow degrades back to 3 steps, which is cosmetic.
export function hasPendingCarry(): boolean {
  return !!(readPendingDescription() || readPendingStructure());
}

// Consume-on-success ONLY (spec req 8/10): every degrade path leaves the keys
// so the GetStarted pre-filled builder remains the backstop.
export function clearPendingCarry(): void {
  try {
    localStorage.removeItem(PENDING_DESC_KEY);
    localStorage.removeItem(PENDING_SPORT_KEY);
    localStorage.removeItem(PENDING_STRUCTURE_KEY);
  } catch { /* storage unavailable */ }
}

export function readPendingDescription(): string {
  try {
    return (localStorage.getItem(PENDING_DESC_KEY) || '').trim();
  } catch {
    return '';
  }
}

export function readPendingSport(): string {
  try {
    return (localStorage.getItem(PENDING_SPORT_KEY) || '').trim().toLowerCase();
  } catch {
    return '';
  }
}

export default function PendingDescriptionBanner() {
  const desc = readPendingDescription();
  if (!desc) return null;
  const shown = desc.length > 140 ? `${desc.slice(0, 138)}…` : desc;
  return (
    <div
      className="mx-auto mb-8 flex max-w-xl items-start gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-left"
      data-testid="pending-desc-banner"
    >
      <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
      <p className="text-sm leading-relaxed text-foreground">
        <span className="text-muted-foreground">You told us: </span>
        <span className="font-medium">&ldquo;{shown}&rdquo;</span>
        <span className="text-muted-foreground">
          . We&apos;ll build this structure right after your organization is created.
        </span>
      </p>
    </div>
  );
}
